import { useTranslation } from "react-i18next";
import { Page } from "../../components/page/Page";
import { Box, useMediaQuery, useTheme } from "@mui/material";
import Grid from '@mui/material/Grid2';
import { useEnvironment } from "@keycloak/keycloak-ui-shared";

import SignInWidget from "./widgets/SignIn";
import LinkedAccountsWidget from "./widgets/LinkedAccounts";
import DLGroupWidget from "./widgets/DLGroup";
import DeviceActivityWidget from "./widgets/DeviceActivity";
import { usePromise } from "../../utils/usePromise";
import { getCountries, getCredentials, getDevices, getGroups, getLinkedAccounts } from "../../api/methods";
import { Environment } from "../../environment";
import { useState } from "react";
import { Country, CredentialContainer, DeviceRepresentation, Group, LinkedAccountRepresentation } from "../../api/representations";
import { getParents } from "../groups/Groups";
import { moveCurrentToTop } from "../account-security/DeviceActivity";
import { useMenu } from "../../providers/MenuProvider";
import { usePersonalInfoContext } from "../../providers/PersonalInfoProvider";
import UserInfo from "./UserInfo";

export default function Dashboard() {
  const { t } = useTranslation();
  const { isOpen } = useMenu();
  const theme = useTheme();
  const isDownLg = useMediaQuery(theme.breakpoints.down('lg'));
  const isDownXl = useMediaQuery(theme.breakpoints.down('xl'));
  const context = useEnvironment<Environment>();
  const { email, attributes, accountType } = usePersonalInfoContext();
  const [linkedAccounts, setLinkedAccounts] = useState<
    LinkedAccountRepresentation[]
  >([]);
  const personalInfo = usePersonalInfoContext();
  const [groups, setGroups] = useState<Group[]>([]);
  const [directMembership, setDirectMembership] = useState(false);
  const [devices, setDevices] = useState<DeviceRepresentation[]>();
  const [country, setCountry] = useState<string>();
  const [credentials, setCredentials] = useState<CredentialContainer[]>();

  const setCountryHandler = (countries: Country[]) => {
    const basedIn = personalInfo.attributes?.based_in;
    const basedInId = Array.isArray(basedIn) ? basedIn[0] : undefined;
    const findedCountry = countries.find((country) => country.id === basedInId);

    setCountry(findedCountry?.name);
  }

  const { loading: groupsLoading } = usePromise(
    (signal) => getGroups({ signal, context }),
    (groups) => {
      if (!directMembership) {
        groups.forEach((el) =>
          getParents(
            el,
            groups,
            groups.map(({ path }) => path),
          ),
        );
      }
      setGroups(groups);
    },
    [directMembership],
  );
      
  const { error: linkedAccountsError, loading: linkedAccountsLoading } = usePromise(
    (signal) => getLinkedAccounts({ signal, context }, {
      first: 0,
      linked: true,
    }),
    setLinkedAccounts,
    [],
  );

  usePromise(
    (signal) => getCountries({ signal, context }),
    setCountryHandler,
    [],
  );

  const { loading: devicesLoading } = usePromise((signal) => getDevices({ signal, context }), moveCurrentToTop(setDevices), []);

  const { loading: credentialsLoading } = usePromise(
    (signal) => getCredentials({ signal, context }),
    setCredentials,
    [],
  );

  const showLinkedAccountsWidget = !linkedAccountsLoading && linkedAccountsError?.statusCode !==403 || context.environment.features.isLinkedAccountsEnabled;

  return (
      <Page title={t("dashboard")}>
        <UserInfo t={t} kcContext={context} country={country} personalEmail={accountType === 'HYBRID' ? attributes.corp_email[0] : personalInfo.email} />

        <Grid container spacing={{ xs: 2, md: 3 }} columns={{ xs: 4, sm: 8, md: 12 }}>
          <Grid size={{ xs: 12, sm: 12, md: isOpen && isDownLg ? 12 : 6, lg: isOpen && isDownXl ? 6 : showLinkedAccountsWidget ? 4 : 6 }}>
            <SignInWidget t={t} resourceUrl={context.environment.resourceUrl} credentials={credentials} userEmail={email} loading={credentialsLoading} />
          </Grid>
          {showLinkedAccountsWidget && (<Grid size={{ xs: 12, sm: 12, md: isOpen && isDownLg ? 12 : 6, lg: isOpen && isDownXl ? 6 : 4 }}>
            <LinkedAccountsWidget t={t} linkedAccounts={linkedAccounts} resourceUrl={context.environment.resourceUrl} loading={linkedAccountsLoading} />
          </Grid>)}
          <Grid size={{ xs: 12, sm: 12, md: showLinkedAccountsWidget ? 12 : isOpen && isDownLg ? 12 : 6, lg: isOpen && isDownXl ? 12 : showLinkedAccountsWidget ? 4 : 6 }}>
            <DLGroupWidget t={t} groups={groups} loading={groupsLoading} />
          </Grid>
        </Grid>
        <Box sx={{ marginTop: 3 }}>
          <DeviceActivityWidget t={t} devices={devices} resourceUrl={context.environment.resourceUrl} loading={devicesLoading} />
        </Box>
        {/* <Modal isOpen={isModalOpen} onClose={personalSigningHandler}>
            <Box sx={dashboardModalBoxStyles}>
              <StyledImage sx={dashboardModalImgStyles} src={`${context.environment.resourceUrl}/images/password_setup.svg`} alt="img" />
              <StyledPersonalPassHeader variant="h5">{t('personalSigningMethod')}</StyledPersonalPassHeader>
              <Typography marginBottom="30px">{t('personalSigningDesc')}</Typography>
              <Button sx={dashboardModalButtonStyles} variant="contained" onClick={personalSigningHandler}>{t('gotIt')}</Button>
            </Box>
        </Modal> */}
      </Page>
  );
}